'use client';

/**
 * PaymentMethodBadges — the row of payment methods shown on STEP 1 of the
 * membership checkout, directly under the Continue button.
 *
 * ── WHY IT EXISTS ───────────────────────────────────────────────────────────
 * Lars, reviewing the checkout on 2026-08-27:
 *
 *   "what will happen when you click pay? Some people might think, oh, if I
 *    click it, money is immediately taken."
 *
 * So the caption is not decoration, it is the whole point:
 * "You choose how to pay on the next step. Nothing is charged yet."
 *
 * ── THE RULE THIS COMPONENT EXISTS TO OBEY ──────────────────────────────────
 * The list MUST be the same list step 2 renders. A hand-maintained array here
 * would drift from pf-platform's checkoutPaymentMethodTypesForCurrency() — and
 * that drift would be silent, promising a method for months after it was
 * removed. So nothing is hard-coded: the methods come from
 *
 *   GET {PLATFORM_ORIGIN}/api/v1/checkout/payment-methods?currency=eur|usd
 *
 * which serves the very function that sets `payment_method_types` on the
 * subscription. One source, two consumers.
 *
 * Wallets (Apple Pay / Google Pay) are the one exception, and they are not an
 * exception to the rule — they are never `payment_method_types` values at all;
 * they ride on "card" inside the Payment Element, and whether they appear is
 * the BROWSER's decision. So they render here only when the caller has actually
 * observed the wallet to be available (`walletsAvailable`, fed by the step-2
 * ExpressCheckoutElement's onReady). Showing them unconditionally would promise
 * a Firefox-on-Windows buyer an Apple Pay button that never appears.
 *
 * ── WHY TEXT AND NOT LOGOS ──────────────────────────────────────────────────
 * The approved design (Team Inbox/checkout-mockup-APPROVED-2026-08-27.html)
 * renders payment methods as plain text rows, not brand artwork, and
 * public/images/checkout/methods/ holds no brand SVGs. Rather than draw
 * approximations of trademarked marks, this renders the same wordmark treatment
 * the approved design uses. Flagged as an open item: if Aria supplies real
 * brand SVGs, only LABELS below changes.
 */

import { useEffect, useState } from 'react';

/**
 * Human labels for the Stripe method ids the server may return, plus the two
 * wallets. Anything unrecognised is skipped rather than shown as a raw id —
 * a new method reaching production as "revolut_pay" would look like a bug.
 */
const LABELS: Record<string, string> = {
  card: 'Card',
  link: 'Link',
  paypal: 'PayPal',
  klarna: 'Klarna',
  revolut_pay: 'Revolut Pay',
  amazon_pay: 'Amazon Pay',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
};

export interface PaymentMethodBadgesProps {
  /** Presentment currency the buyer has chosen. */
  currency: 'EUR' | 'USD';
  /** Absolute pf-platform origin. Empty string disables the fetch entirely. */
  platformOrigin: string;
  /**
   * Wallets the BROWSER has been observed to offer. Empty until step 2 has
   * mounted an ExpressCheckoutElement at least once — deliberately, because an
   * unobserved wallet is a promise we cannot keep.
   */
  walletsAvailable?: readonly string[];
}

export default function PaymentMethodBadges({
  currency,
  platformOrigin,
  walletsAvailable = [],
}: PaymentMethodBadgesProps) {
  const [methods, setMethods] = useState<string[] | null>(null);

  useEffect(() => {
    if (!platformOrigin) return;
    let cancelled = false;
    const url = `${platformOrigin}/api/v1/checkout/payment-methods?currency=${currency.toLowerCase()}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('unavailable'))))
      .then((data: { methods?: unknown }) => {
        if (cancelled) return;
        setMethods(Array.isArray(data.methods) ? (data.methods as string[]) : null);
      })
      .catch(() => {
        // Render the caption alone rather than a stale or invented list. The
        // sentence is the part that answers Lars's question; the marks are
        // supporting evidence, and wrong evidence is worse than none.
        if (!cancelled) setMethods(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currency, platformOrigin]);

  const shown = [...(methods ?? []), ...walletsAvailable].filter((id) => id in LABELS);

  return (
    <div className="cms-pmbadges">
      {shown.length > 0 && (
        <ul className="cms-pmbadge-list" aria-label="Payment methods available on the next step">
          {shown.map((id) => (
            <li key={id} className="cms-pmbadge">
              {LABELS[id]}
            </li>
          ))}
        </ul>
      )}
      <p className="cms-pmbadge-note">
        You choose how to pay on the next step. Nothing is charged yet.
      </p>
    </div>
  );
}
